import { useTranslation } from 'react-i18next';
import { ErrorState, Skeleton } from '@/components/ui';
import { useConsoleBreadcrumb } from '@/components/shell/ConsoleBreadcrumbContext';
import { OrgAdminApiError } from '@/api/orgAdmin';
import { AccountAuditPanel } from '@/pages/console/audit/AccountAuditPanel';
import { DeleteAccountDialog } from '../DeleteAccountDialog';
import { LockAccountDialog } from '../LockAccountDialog';
import { OneTimeSecretModal } from '../OneTimeSecretModal';
import { ResetAccountDrawer } from '../ResetAccountDrawer';
import { AccountProfileHeader } from './AccountProfileHeader';
import { AccountProfileOverviewTab } from './AccountProfileOverviewTab';
import { AccountProfilePermissionsTab } from './AccountProfilePermissionsTab';
import { AccountProfileTabs } from './AccountProfileTabs';
import { useAccountProfilePage } from './useAccountProfilePage';

/**
 * EAA-1820 (frame 72): full account profile at `/console/accounts/:sub` —
 * replaces the list-row drawer as the primary drill-in. Tab state lives in
 * `?tab=` so deep links and back/forward survive reload.
 */
export function AccountProfilePage() {
    const { t } = useTranslation();
    const vm = useAccountProfilePage();

    // EAA-1846 (frame 95): breadcrumb moved to the shared topbar. Called
    // unconditionally (Rules of Hooks) — an empty trail when access is denied
    // renders nothing, matching what the old in-body version did in that branch.
    useConsoleBreadcrumb(
        vm.canView
            ? [
                  { label: t('console.accounts.profile.breadcrumbOperations'), to: '/console' },
                  { label: t('console.accounts.page.title'), to: '/console/accounts' },
                  { label: vm.detail ? vm.displayName : t('common.loading') },
              ]
            : []
    );

    if (!vm.canView) {
        return (
            <main className="portal-admin portal-console-page">
                <ErrorState description={t('console.accounts.profile.accessDenied')} />
            </main>
        );
    }

    const { detailQuery, detail, displayName } = vm;
    const loadError =
        detailQuery.error instanceof OrgAdminApiError
            ? detailQuery.error.message
            : t('console.accounts.profile.loadError');

    return (
        <>
            <main className="portal-admin portal-console-page acc-profile-page">

                {detailQuery.isLoading && (
                    <div className="acc-profile-loading" role="status" aria-live="polite">
                        <span className="portal-sr-only">{t('common.loading')}</span>
                        <Skeleton height="5rem" />
                    </div>
                )}

                {detailQuery.isError && (
                    <ErrorState
                        description={loadError}
                        onRetry={() => detailQuery.refetch()}
                        retrying={detailQuery.isFetching}
                    />
                )}

                {detail && (
                    <div className="acc-profile-body">
                        <AccountProfileHeader
                            detail={detail}
                            displayName={displayName}
                            onReset={vm.openReset}
                            onLock={vm.openLock}
                            onDelete={vm.openDelete}
                            canReset={vm.canReset}
                            canLock={vm.canLock}
                            canDelete={vm.canDelete}
                            lockVisible={detail.enabled}
                        />

                        <AccountProfileTabs
                            active={vm.tab}
                            onSelect={vm.setTab}
                            placementCount={detail.placements.length}
                            auditCount={vm.canViewAudit ? vm.auditCount : undefined}
                            showAuditTab={vm.canViewAudit}
                        />

                        <div className="acc-profile-tab-panel">
                            {vm.tab === 'overview' && (
                                <AccountProfileOverviewTab
                                    detail={detail}
                                    lastLoginAvailable={detail.last_login_available ?? false}
                                    recentAudit={vm.recentAudit}
                                    onViewAllAudit={() => vm.setTab('audit')}
                                />
                            )}
                            {vm.tab === 'permissions' && <AccountProfilePermissionsTab detail={detail} />}
                            {vm.tab === 'audit' && vm.canViewAudit && (
                                <AccountAuditPanel lockedUsername={detail.username} />
                            )}
                            {vm.tab === 'audit' && !vm.canViewAudit && (
                                <p className="portal-admin-hint">{t('console.accounts.profile.accessDenied')}</p>
                            )}
                        </div>
                    </div>
                )}
            </main>

            <OneTimeSecretModal
                secret={
                    vm.resetResult
                        ? {
                              kind: 'reset',
                              username: vm.resetResult.username,
                              tempPassword: vm.resetResult.temp_password ?? undefined,
                          }
                        : null
                }
                onClose={() => vm.setResetResult(null)}
            />

            {vm.canReset && (
                <ResetAccountDrawer
                    open={vm.resetOpen}
                    target={detail ?? null}
                    ident={vm.resetIdent}
                    credential={vm.resetCredential}
                    pending={vm.resetMutation.isPending}
                    error={vm.resetError}
                    onIdentChange={vm.setResetIdent}
                    onClose={() => vm.setResetOpen(false)}
                    onSubmit={({ endSessions }) => vm.resetMutation.mutate({ endSessions })}
                />
            )}

            {vm.canLock && (
                <LockAccountDialog
                    row={vm.lockTarget}
                    pending={vm.lockMutation.isPending}
                    error={vm.lockError}
                    onClose={() => vm.setLockTarget(null)}
                    onConfirm={(endSessions) =>
                        vm.lockTarget &&
                        vm.lockMutation.mutate({
                            sub: vm.lockTarget.sub,
                            username: vm.lockTarget.username,
                            endSessions,
                        })
                    }
                />
            )}

            {vm.canDelete && (
                <DeleteAccountDialog
                    row={vm.deleteTarget}
                    pending={vm.deleteMutation.isPending}
                    error={vm.deleteError}
                    onClose={() => vm.setDeleteTarget(null)}
                    onConfirm={(accountSub) => vm.deleteMutation.mutate(accountSub)}
                />
            )}
        </>
    );
}

export default AccountProfilePage;
